/**
 * Reading the protocol for what a CRF field must carry (split out of crfAuthority.ts so
 * the CRF Builder approval gate can import it: it is pure, and crfAuthority reaches the
 * CDISC catalog, which the browser build cannot).
 * Moved verbatim; crfAuthority.ts re-exports every name, so no caller changes.
 */

import { namedSeverityScale, printedSeverityInStatement, gradedSeverityWords } from "./standardScales";
import { buildDoseLevels, isDoseLevelQuestion } from "./doseLevels";
import { isNeverAHeadingLine, LEADER_LINE, tocLineFlags } from "./protocolHeadings";
import { studyInterventions } from "../studyProfile/studyProducts";

export type AuthorityConcept =
  | "sex" | "race" | "ethnicity" | "severity" | "causality" | "outcome" | "action"
  | "disposition" | "yesno" | "pregnancyResult" | "pregnancyOutcome" | "dipstickResult" | "doseLevel";

export type CodeLayer = "sponsor dictionary" | "protocol" | "CDASH" | "platform default";

/**
 * `definition` is the protocol's own explanation of one answer ("does not interfere with usual activities" for
 * "Mild"). The option label stays the short word the form prints; the Completion Guide reads the definition
 * (crfGuide/inputAdapter.ts) and prints it under that question (2026-10-30, Jim).
 */
export interface AuthOption { code: number | string; label: string; definition?: string }
export interface ResolvedList { options: AuthOption[]; layer: CodeLayer; source: string }


/** The shape this module reads and writes. Anything else on the variable rides along untouched. */
export interface AuthVariable {
  varName: string;
  questionText: string;
  responseType: string;
  options?: Array<{ code?: number | string; label: string }>;
  codesFrom?: string;
  [k: string]: unknown;
}

// ── Reading the protocol ───────────────────────────────────────────────────

export interface Sourced { text: string; section?: string; field?: string; codes?: AuthOption[] }

/**
 * An option label that repeats its own code ("0 = Normal", "97 - Not done" on the option coded 0 / 97) shows the
 * code twice on the form ("0=0 = Normal"). Returns the label without that leading "<code> =" or "<code> -".
 */
export function stripOwnCode(code: number | string | undefined, label: string): string {
  const c = String(code ?? "").trim();
  if (!c) return label;
  const esc = c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // "<code> =" always repeats the code. A dash repeats it only as a separator (a space, then a non-digit): "1-2 times", "0-3 months" and "1 - 2 times" are ranges.
  const m = new RegExp(`^\\s*${esc}\\s*=\\s*(?=\\S)|^\\s*${esc}\\s+[-–—]\\s+(?=\\D)`).exec(label);
  if (!m) return label;
  const rest = label.slice(m[0].length);
  return rest.charAt(0).toLowerCase() === rest.charAt(0) ? cap(rest) : rest;
}

export function factsOf(analysis: Record<string, unknown> | null | undefined, key: string): Sourced[] {
  const raw = (analysis ?? {})[key];
  if (!Array.isArray(raw)) return [];
  const out: Sourced[] = [];
  for (const f of raw) {
    if (typeof f === "string" && f.trim()) out.push({ text: f.trim() });
    else if (f && typeof f === "object") {
      const t = (f as { text?: unknown }).text;
      const s = (f as { section?: unknown }).section;
      const field = (f as { field?: unknown }).field;
      const rawCodes = (f as { codes?: unknown }).codes;
      const codes: AuthOption[] = Array.isArray(rawCodes)
        ? rawCodes.flatMap((c) => {
            const code = (c as { code?: unknown })?.code;
            const label = (c as { label?: unknown })?.label;
            return (typeof code === "string" || typeof code === "number") && String(code).trim() !== "" && typeof label === "string" && label.trim()
              ? [{ code, label: stripOwnCode(code, label.trim()) }] : [];
          })
        : [];
      if (typeof t === "string" && t.trim()) {
        out.push({
          text: t.trim(),
          ...(typeof s === "string" && s.trim() ? { section: s.trim() } : {}),
          ...(typeof field === "string" && field.trim() ? { field: field.trim() } : {}),
          ...(codes.length >= 2 ? { codes } : {}),
        });
      }
    }
  }
  return out;
}

export const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const OPEN = "([";
const CLOSE = ")]";

/** Split on separators that sit OUTSIDE brackets and quotes: "Mild (itch; rash); Marked" has two pieces, not three. */
export function splitOutsideBrackets(text: string, isSep: (ch: string, rest: string) => boolean): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote = "";
  let cur = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quote) { if (ch === quote) quote = ""; cur += ch; continue; }
    // A quote opens only at the start of a word, so an apostrophe inside "sponsor's" is not a quote.
    if ((ch === '"' || ch === "“") && (i === 0 || /[\s(\[,;:]/.test(text[i - 1]))) { quote = ch === "“" ? "”" : ch; cur += ch; continue; }
    if (OPEN.includes(ch)) depth++;
    else if (CLOSE.includes(ch) && depth > 0) depth--;
    if (depth === 0 && isSep(ch, text.slice(i + 1))) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

/** Remove balanced (...) and [...] groups, innermost first. */
const stripBrackets = (s: string): string => {
  let prev = "";
  let cur = s;
  while (cur !== prev) { prev = cur; cur = cur.replace(/\([^()[\]]*\)|\[[^()[\]]*\]/g, " "); }
  return cur;
};

/** Trim punctuation around a label, but keep a bracket that closes one the label opened. */
export const clean = (s: string): string => {
  let t = s.replace(/\s+/g, " ").trim();
  for (let guard = 0; guard < 20; guard++) {
    const before = t;
    t = t.replace(/^[\s"'\-–—]+/, "").replace(/[\s"'.,:;]+$/, "");
    const opens = (t.match(/[([]/g) ?? []).length;
    const closes = (t.match(/[)\]]/g) ?? []).length;
    if (closes > opens && /[)\]]$/.test(t)) t = t.slice(0, -1);
    else if (opens > closes && /^[([]/.test(t)) t = t.slice(1);
    if (t === before) break;
  }
  return t.trim();
};

/** "1=Yes, 0=No" or "Grade 1 = mild; Grade 2 = moderate": every piece must be a code=label pair. */
export function parseCodePairs(text: string): AuthOption[] | null {
  // The whole list wrapped in brackets: "a 3-point scale (1 = mild, 2 = moderate, 3 = severe)".
  const wrapped = /[([]([^()[\]]*=[^()[\]]*=[^()[\]]*)[)\]]/.exec(text);
  if (wrapped && !text.replace(wrapped[0], " ").includes("=")) return parseCodePairs(wrapped[1]);
  const pieces = splitOutsideBrackets(text, (ch) => ch === ";" || ch === "\n")
    .flatMap((p) => splitOutsideBrackets(p, (ch, rest) => ch === "," && /^\s*\S{1,12}\s*=/.test(rest)));
  const out: AuthOption[] = [];
  for (const piece of pieces) {
    const m = /(?:^|\s)(\S{1,8})\s*=\s*(.+)$/.exec(piece.trim());
    if (!m) { if (out.length > 0 || pieces.length === 1) return null; continue; }
    // Wrapping punctuation is not part of a code: "(1 = mild" has the code 1.
    let code: number | string = m[1].replace(/^[^A-Za-z0-9+]+|[^A-Za-z0-9+]+$/g, "");
    if (!code) return null;
    let label = clean(m[2]);
    // A label whose bracket never closes ("Grade 1 (mild") was cut apart: no list rather than a broken one.
    if (!label || label.length > 120 || !balancedBrackets(label)) return null;
    const gradeBefore = /grade\s*$/i.test(piece.slice(0, piece.indexOf(m[1])).trim());
    if (/^\d+$/.test(String(code))) {
      code = Number(code);
      if (gradeBefore) label = `Grade ${code} - ${cap(label)}`;
    }
    label = stripOwnCode(code, label);
    out.push({ code, label: gradeBefore ? label : cap(label) });
  }
  const codes = new Set(out.map((o) => String(o.code)));
  return out.length >= 2 && codes.size === out.length ? out : null;
}

/** True when every bracket a label opens is closed ("Grade 1 (mild" is a broken label, not an answer). */
export const balancedBrackets = (s: string): boolean => (s.match(/[([]/g) ?? []).length === (s.match(/[)\]]/g) ?? []).length;

/** Words that make a piece of text a sentence, not an answer ("event that completely incapacitates", "will be used"). */
const PROSE_WORD = /\b(?:is|are|was|were|will|would|shall|should|may|must|can|could|be|been|being|has|have|had|that|which|who|whenever|wherever|corresponding|indicates?|makes?)\b/i;
const PROSE_OPEN = /^(?:but|however|although|though|because|since|if|when|whereas|while|unless|e\.g\.?|i\.e\.?|for example|such as|including|etc|at|per|by|for|via|from|without|then)\b/i;
/**
 * Words that say how an item is COLLECTED ("Self-reported, per local regulations", "entered as read",
 * "Recorded at each visit", "serum if positive"). "Not reported", "Not collected", "Not assessed" are answers.
 */
const COLLECTION_WORD = /(?<!\bnot\s)\b(?:recorded|assessed|collected|entered|reported|identified|measured|performed|obtained|documented)\b|\bself-(?:report|identif|assess|declar|complet)|\bif\b/i;
/** Published answers that carry "or" inside them: never split, even as the final item of a list. */
const COMPOUND_ANSWER = /^(?:black or african american|american indian or alaska native|native hawaiian or (?:other )?pacific islander|(?:not )?hispanic or latino)$/i;

/** The label of ONE level of a scale ("Severe", "Possible", "No reasonable possibility"): a statement it opens is its definition. */
function isLevelName(lead: string): boolean {
  const l = clean(lead);
  if (!l || l.split(/\s+/).length > 4 || /relationship|relatedness|causality|scale|categor|grading|assess/i.test(l)) return false;
  return SEVERITY_LABEL.test(l) || CAUSALITY_LABEL.test(l);
}

/**
 * The answers in a list body, split by the separators the protocol used, or null when they cannot be told apart.
 * ";" wins, then ","; inside such a list "or", "and" and "/" belong to the answer, except that "or"/"and" join the
 * last two answers of a comma list ("mild, moderate or severe"). With neither, "or" separates only when `orAllowed`
 * (a field name or a lead-in came before it), and "/" only when it is the sole separator ("Yes/No").
 */
function splitAnswers(body: string, orAllowed: boolean): string[] | null {
  const semis = splitOutsideBrackets(body, (ch) => ch === ";");
  if (semis.length >= 2) {
    // "Asian; Multiple, or Other": the comma list inside a ";" answer cannot be told apart from the answer.
    if (semis.some((x) => splitOutsideBrackets(x, (ch, rest) => ch === "," && /^\s*(?:or|and)\s/i.test(rest)).length > 1)) return null;
    const last = semis.length - 1;
    return semis.map((s, i) => (i === last ? s.trim().replace(/^(?:or|and)\s+/i, "") : s));
  }
  const commas = splitOutsideBrackets(body, (ch) => ch === ",");
  if (commas.length >= 2) {
    const items = commas.map((s) => s.trim());
    // "a, and b, c": an answer after the one that closes the list is the sentence going on, not an answer.
    if (items.some((s, i) => i > 0 && i < items.length - 1 && /^(?:or|and)\s/i.test(s))) return null;
    const lastRaw = items[items.length - 1].replace(/^(?:or|and)\s+/i, "");
    const head = items.slice(0, -1);
    if (head.length === 1 && /^either\s/i.test(head[0])) head[0] = head[0].replace(/^either\s+/i, "");
    if (COMPOUND_ANSWER.test(clean(lastRaw))) return [...head, lastRaw];
    const tail = splitOutsideBrackets(lastRaw, (ch, rest) => /\s/.test(ch) && /^(?:or|and)\s/i.test(rest))
      .map((s) => s.replace(/^(?:or|and)\s+/i, ""));
    // Only one more answer joins the last: "moderate or severe", never "a or b or c".
    if (tail.length > 2) return null;
    return [...head, ...tail];
  }
  if (orAllowed && !COMPOUND_ANSWER.test(clean(body))) {
    const ors = splitOutsideBrackets(body, (ch, rest) => /\s/.test(ch) && /^or\s/i.test(rest)).map((s) => s.replace(/^or\s+/i, ""));
    if (ors.length === 2) return [ors[0].replace(/^either\s+/i, ""), ors[1]];
    if (ors.length > 2) return null;
  }
  if (/\s(?:or|and)\s/i.test(stripBrackets(body))) return null;
  const slashes = splitOutsideBrackets(body, (ch) => ch === "/");
  return slashes.length >= 2 ? slashes : null;
}

/** The answers, cleaned and checked: 2 to 12 short, distinct answers that are not sentences, or null. */
function checkedAnswers(pieces: string[] | null, dropExplanations = true): string[] | null {
  if (!pieces) return null;
  // A bracketed explanation after an answer is not part of its label: "Low (under 5 units, no action)" is "Low".
  const items = pieces.map((s) => clean(dropExplanations ? stripBrackets(s) : s));
  if (items.length < 2 || items.length > 12) return null;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const i of items) {
    if (!i || i.split(/\s+/).length > 6 || i.length > 60) return null;
    // "may be related" / "could be related" is a causality answer; elsewhere "may" and "be" mark a sentence.
    if (PROSE_WORD.test(i.replace(/\b(?:may|could|might)\s+be\s+(?=(?:causally\s+)?related\b)/i, "")) || PROSE_OPEN.test(i) || COLLECTION_WORD.test(i) || /[:=]/.test(i) || !balancedBrackets(i)) return null;
    const k = i.toLowerCase();
    if (seen.has(k)) return null;
    seen.add(k);
    out.push(cap(i));
  }
  return out;
}

const LEAD_IN = /\b(?:graded as|rated as|classified as|recorded as|stored as|coded as|as|into|using|scale of|graded)\s+(.+)$/i;

/**
 * The list one sentence states, or null.
 *
 * A BRACKET IS NEVER THE LIST (Jim, 2026-10-17). "Action taken with study drug (dose level 1 or dose level 2)",
 * "Pregnancy test (serum or urine)" and "Action taken ... (none, dose adjusted, ...)" look alike; every rule tried
 * for telling a bracketed answer list from an explanation leaked in review. So a bracket is always an explanation:
 * separators inside it are ignored, and a statement whose only list is in a bracket gives null, which keeps the
 * model's list with the visible "please check" note.
 */
function listInSentence(sentence: string): SentenceList | null {
  const s = sentence.replace(/\s+/g, " ").trim().replace(/[.;]+$/, "").trim();
  if (!s) return null;
  const anchored = (items: string[] | null): SentenceList | null => (items ? { items, unanchoredCommas: false } : null);
  // "<field or level>: answers" and "<field> - answers". A level name before the colon or dash makes a definition.
  const colons = splitOutsideBrackets(s, (ch) => ch === ":");
  if (colons.length > 2) return null;
  if (colons.length === 2) {
    if (isLevelName(stripBrackets(colons[0]))) return null;
    return anchored(checkedAnswers(splitAnswers(colons[1], true)));
  }
  // A dash with a space after it ("process – Related"), never a hyphen inside a word ("life-threatening", "1-5").
  const dashes = splitOutsideBrackets(s, (ch, rest) => /[-–—]/.test(ch) && /^\s/.test(rest));
  if (dashes.length > 2) return null;
  if (dashes.length === 2 && /\s$/.test(dashes[0])) {
    if (isLevelName(stripBrackets(dashes[0]))) return null;
    return anchored(checkedAnswers(splitAnswers(dashes[1], true)));
  }
  // "Severity is graded as mild, moderate or severe": a lead-in phrase before the list (never inside a ";" list, and
  // never inside a bracket: "Race (classified as White, Asian)" has no list outside its bracket).
  const top = stripBrackets(s).replace(/\s+/g, " ").trim();
  const lead = LEAD_IN.exec(top);
  if (lead && !/[,;]/.test(top.slice(0, lead.index)) && !top.includes(";")) {
    return anchored(checkedAnswers(splitAnswers(lead[1], true)));
  }
  const items = checkedAnswers(splitAnswers(s, false));
  // No colon, dash or lead-in, and only commas: "Serum at screening, urine at all other visits" has the shape of a list.
  return items ? { items, unanchoredCommas: !top.includes(";") && top.includes(",") } : null;
}

/**
 * "mild, moderate or severe" -> Mild, Moderate, Severe. Reads the list where the protocol puts it and never
 * splits an answer apart; a definition of one level, a field name or prose is not a list. When unsure, null:
 * the caller then keeps the model's list and says it was not read confidently.
 */
export function parseLabelList(text: string): string[] | null {
  return readSentenceList(text)?.items ?? null;
}

/**
 * parseLabelList for the topic readers (outcome, pregnancy, dipstick, race, ethnicity, action, sex): a comma list
 * with no anchor (no colon, spaced dash or lead-in, and no ";") is not read, because it cannot be told apart from a
 * sentence about how the item is collected. A stored field that names the item is an anchor too (the readers use
 * parseLabelList then). The severity and causality scales keep reading it: their levels must be level words.
 */
export function anchoredLabelList(text: string): string[] | null {
  const r = readSentenceList(text);
  return r && !r.unanchoredCommas ? r.items : null;
}

interface SentenceList { items: string[]; unanchoredCommas: boolean }

function readSentenceList(text: string): SentenceList | null {
  // One list per statement: a second sentence ("... indígena. Not collected in Japan.") is not part of it, and two
  // sentences that each state a list cannot be told apart.
  const sentences = String(text ?? "").split(/(?<=[.!?])\s+(?=[A-Z])/);
  const lists = sentences.map(listInSentence).filter((l): l is SentenceList => !!l);
  return lists.length === 1 ? lists[0] : null;
}

export const sectionOf = (facts: Sourced[]): string => {
  const s = facts.find((f) => f.section)?.section;
  return s ? `protocol section ${s.replace(/^(section|§)\s*/i, "")}` : "protocol";
};

/** Codes 1..n in the order the protocol states them. */
const numbered = (labels: string[]): AuthOption[] => labels.map((label, i) => ({ code: i + 1, label }));

/** The fact's own parsed codes when the extractor gave them, else code=label pairs read from its text. */
function factPairs(f: Sourced): AuthOption[] | null {
  return f.codes && f.codes.length >= 2 ? f.codes : parseCodePairs(f.text);
}

/**
 * A severity or causality list is made of RECOGNISED words, or it is not a
 * scale at all: prose such as "graded 1 to 5 per CTCAE v5.0, where Grade 3 or
 * higher requires reporting" splits into pieces that are not categories.
 */
export const SEVERITY_LABEL = /^(?:mild|moderate|severe|life[- ]threatening|disabling|death|fatal|grade\s*[1-5](?:\s*[-:(]?\s*(?:mild|moderate|severe|life[- ]threatening|disabling|death|fatal)\)?)?)$/i;
export const CAUSALITY_LABEL = /related|unrelated|unlikely|possib|probab|definite|certain|plausible|suspected|doubtful|no relationship|not suspected|conditional/i;

/** The label of a fact that states ONE level of a scale: the text before its bracketed explanation. */
const levelLabel = (text: string): string => clean(text.split(/\s*[:(\[]\s*|\s+-\s+|\.\s/)[0]);

/**
 * One level written with its own code ("1 = Mild (does not interfere with usual activities)", "1: Mild",
 * "1 - Mild"): the protocol's code, the short level word, and the protocol's definition of it. The word before the
 * explanation is the label; the explanation goes to the Completion Guide, not onto the form.
 */
function codedLevel(text: string): { code: number; label: string; definition?: string } | null {
  const m = /^\s*(\d{1,2})\s*(?:=|:|[-–—])\s*([A-Za-z].*)$/.exec(text.trim());
  if (!m) return null;
  // "1 = Grade 1 (Mild)": the grade label is the form's own wording and stays whole (as shortLevels keeps it).
  if (/^grade\s*\d/i.test(m[2].trim())) return { code: Number(m[1]), label: clean(m[2]) };
  const label = levelLabel(m[2]);
  if (!label) return null;
  const rest = m[2].trim().slice(m[2].trim().toLowerCase().indexOf(label.toLowerCase()) + label.length);
  const definition = clean(rest.replace(/^[\s:([–—-]+/, "").replace(/[\s)\].]+$/, ""));
  return { code: Number(m[1]), label: cap(label), ...(definition ? { definition } : {}) };
}

/**
 * A level that carries its explanation in the label ("Mild (does not interfere with usual activities)") keeps the
 * short word as the label and the explanation as its definition, when every shortened word is a level of the
 * scale. A label such as "Grade 1 (Mild)" is the form's own wording and stays whole.
 */
function shortLevels(options: AuthOption[], valid: (label: string) => boolean): AuthOption[] {
  const shortened = options.map((o) => {
    const m = /^([A-Za-z][A-Za-z -]*?)\s*\((.+)\)\s*\.?$/.exec(o.label.trim());
    if (!m || /^grade\b/i.test(m[1]) || !valid(clean(m[1]))) return null;
    return { ...o, label: cap(clean(m[1])), definition: o.definition ?? clean(m[2]) };
  });
  return shortened.every((x) => x) ? (shortened as AuthOption[]) : options;
}

/** One scale: from one fact as pairs, one category per fact, or from one fact as a list. Lists must pass `valid`. */
function scaleFromFacts(facts: Sourced[], valid: (label: string) => boolean): { options: AuthOption[]; facts: Sourced[] } | null {
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs) return { options: shortLevels(pairs, valid), facts: [f] };
  }
  // One level per fact, each written with its own code ("1 = Mild (...)"): the protocol's codes are kept.
  if (facts.length >= 2) {
    const levels = facts.map((f) => codedLevel(f.text));
    if (levels.every((l): l is NonNullable<typeof l> => !!l && valid(l.label))
      && new Set(levels.map((l) => l.code)).size === levels.length && new Set(levels.map((l) => l.label.toLowerCase())).size === levels.length) {
      return { options: levels.map((l) => ({ code: l.code, label: l.label, ...(l.definition ? { definition: l.definition } : {}) })), facts };
    }
  }
  // One level per fact ("Possible (a plausible time relationship, but another cause is plausible)"): an
  // explanation with a comma in it is not a second level, so this reading comes before a list reading.
  if (facts.length >= 2) {
    const labels = facts.map((f) => levelLabel(f.text));
    if (labels.every((l) => l && l.split(/\s+/).length <= 6 && !/[,;/]|\bor\b/i.test(l) && valid(l)) && new Set(labels.map((l) => l.toLowerCase())).size === labels.length) {
      return { options: numbered(labels.map(cap)), facts };
    }
  }
  for (const f of facts) {
    const list = parseLabelList(f.text);
    if (list && list.every(valid)) return { options: numbered(list), facts: [f] };
  }
  return null;
}

const GRADE_WORD = /^(?:mild|moderate|severe|life[- ]threatening|disabling|death|fatal)\b/i;

/**
 * A grade scale held as ONE FACT PER GRADE ("Grade 1 mild", ..., "Grade 5 death related to AE"): the levels
 * become "Grade 1 (mild)" with the grade as the code. Facts that are not a grade level are ignored (a closing
 * note about the scale); fewer than two levels, or a repeated grade, is not a scale.
 */
function gradeLevelsFromFacts(facts: Sourced[]): { options: AuthOption[]; facts: Sourced[] } | null {
  const levels = new Map<number, string>();
  const used: Sourced[] = [];
  for (const f of facts) {
    const m = /^grade\s*([1-5])\b[\s:\-\u2013(]*(.*)$/i.exec(f.text.trim());
    if (!m || !GRADE_WORD.test(m[2])) continue;
    const n = Number(m[1]);
    if (levels.has(n)) return null;
    const word = clean(m[2].split(/\s+(?:related|due|attributed|resulting)\b|[(;,]/i)[0]).toLowerCase();
    levels.set(n, `Grade ${n} (${word})`);
    used.push(f);
  }
  if (levels.size < 2) return null;
  return { options: [...levels.entries()].sort((a, b) => a[0] - b[0]).map(([code, label]) => ({ code, label })), facts: used };
}

const isYes = (l: string) => /^yes\b|^y$/i.test(l.trim());
const isNo = (l: string) => /^no\b|^n$/i.test(l.trim());

/** The protocol's coding conventions, as sourced facts (the fact class may be absent). */
function conventions(analysis: Record<string, unknown> | null | undefined): Sourced[] {
  return factsOf(analysis, "codingConventions");
}

function yesNoFromProtocol(analysis: Record<string, unknown> | null | undefined): ResolvedList | null {
  const facts = conventions(analysis);
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs && pairs.some((o) => isYes(o.label)) && pairs.some((o) => isNo(o.label))) {
      const yes = pairs.find((o) => isYes(o.label))!;
      const no = pairs.find((o) => isNo(o.label))!;
      return { options: [{ code: yes.code, label: "Yes" }, { code: no.code, label: "No" }], layer: "protocol", source: sectionOf([f]) };
    }
    if (/\bY\s*\/\s*N\b/.test(f.text) || /\byes\s*\/\s*no\b.*\bY\b.*\bN\b/i.test(f.text)) {
      return { options: [{ code: "Y", label: "Yes" }, { code: "N", label: "No" }], layer: "protocol", source: sectionOf([f]) };
    }
    if (/\b1\s*\/\s*0\b/.test(f.text) && /yes|no/i.test(f.text)) {
      return { options: [{ code: 1, label: "Yes" }, { code: 0, label: "No" }], layer: "protocol", source: sectionOf([f]) };
    }
  }
  return null;
}

function sexFromProtocol(analysis: Record<string, unknown> | null | undefined): ResolvedList | null {
  for (const f of conventions(analysis)) {
    const pairs = factPairs(f);
    if (pairs && pairs.some((o) => /^male/i.test(o.label)) && pairs.some((o) => /^female/i.test(o.label))) {
      return { options: pairs, layer: "protocol", source: sectionOf([f]) };
    }
    if (/\bsex\b/i.test(f.text) && /\bM\s*\/\s*F\b/.test(f.text)) {
      return { options: [{ code: "M", label: "Male" }, { code: "F", label: "Female" }], layer: "protocol", source: sectionOf([f]) };
    }
  }
  return null;
}

/**
 * What a convention is ABOUT: its stored field and the words before its list ("The reason for discontinuation is
 * recorded as one of: ..." is about the reason for discontinuation). NEVER its answers. Until 2026-10-30 a topic was
 * matched on the field AND THE WHOLE TEXT, so a discontinuation list with the answer "Pregnancy" became the pregnancy
 * test result, and an answer "Fatal outcome" would have become the AE outcome (HVU-SLP-07 rehearsal, Clinical Dev).
 */
const ABOUT_CUT = /[:=(]|\b(?:is|are|was|were|recorded as|coded as|graded as|classified as|rated as|one of|either|as follows)\b/i;
export const aboutOf = (f: Sourced): string => {
  const cut = f.text.search(ABOUT_CUT);
  return (cut > 0 ? f.text.slice(0, cut) : f.text).trim();
};
/**
 * Field plus the words before the list. A text with NO heading before its list (no colon, equals sign, bracket or
 * "is / recorded as") is a sentence, and when the extractor gave it a field the field is what it is about: measured on
 * an end-of-study sentence offering "discontinue stimulation" among four post-trial options, which the words alone
 * made a discontinuation list (2026-10-30).
 */
const aboutText = (f: Sourced): string => {
  const about = aboutOf(f);
  const words = about.split(/\s+/).length;
  // A heading is short (ten words or fewer), or it ends in a colon / equals sign that opens the list in the first
  // 220 characters (a long heading over a generic field such as "Coded answers" is still a heading).
  const colonHead = /[:=]/.test(f.text.slice(0, 220)) && words <= 30;
  const headed = f.text.search(ABOUT_CUT) > 0 && (words <= 10 || colonHead);
  return `${f.field ?? ""} ${headed || !f.field ? about : ""}`;
};

interface Topic { test(s: string): boolean }
/**
 * Where in the conventions a result list is named (matched on what a convention is about, see aboutOf). A pregnancy
 * TEST RESULT and a pregnancy OUTCOME are different questions, and neither is the adverse event outcome.
 */
const RESULT_TOPIC: Record<"outcome" | "pregnancyResult" | "pregnancyOutcome" | "dipstickResult" | "disposition", Topic> = {
  outcome: { test: (s) => /\boutcome\b/i.test(s) && !/pregnan/i.test(s) },
  pregnancyResult: { test: (s) => /pregnan/i.test(s) && !/\boutcome\b/i.test(s) },
  pregnancyOutcome: { test: (s) => /pregnan/i.test(s) && /\boutcome\b/i.test(s) },
  dipstickResult: { test: (s) => /dipstick|urinalysis/i.test(s) },
  // The reason a participant ended the study or the treatment: not a dose hold, a visit, a test not done.
  disposition: { test: (s) => /discontinu|withdraw|terminat|end of (?:the )?(?:study|treatment|participation)|early (?:exit|end)|disposition|study completion|completion status/i.test(s) && !/\bdose|\bhold\b|\bheld\b|interrupt/i.test(s) },
};

/** A convention whose whole list is Yes and No: a yes/no question, not a list of results or outcomes. */
function isYesNoOnly(f: Sourced): boolean {
  const labs = (factPairs(f)?.map((o) => o.label) ?? parseLabelList(f.text) ?? []).map((l) => l.trim());
  return labs.length === 2 && labs.some(isYes) && labs.some(isNo);
}

// ── The reason a participant ended: the TREATMENT or the STUDY ──────────────────────────────────────────
// A protocol can state one list for stopping the treatment and another for leaving the study. The first build took
// the first convention for both questions (checker, 2026-10-30).
const TREATMENT_WORDS = /\b(?:treatment|drug|product|intervention|therapy|dosing|medication)\b/i;
const STUDY_WORDS = /\b(?:study|trial|participation|early termination)\b/i;
/** "treatment", "study", or null when the words name both or neither ("study treatment" is the treatment). */
function scopeOf(s: string, name = ""): "treatment" | "study" | null {
  const t = s.replace(/\bstudy[- ]?(?:drugs?|treatments?|products?|interventions?|medications?|therapy)\b/gi, "treatment");
  const tr = TREATMENT_WORDS.test(t);
  const st = STUDY_WORDS.test(t);
  if (tr && !st) return "treatment";
  if (st && !tr) return "study";
  if (!tr && !st) return /^EOT/i.test(name) ? "treatment" : /^EOS/i.test(name) ? "study" : null;
  return null;
}
const listKey = (o: AuthOption[]) => o.map((x) => x.label.trim().toLowerCase()).join("|");

function dispositionLists(analysis: Record<string, unknown> | null | undefined, v?: AuthVariable): { chosen: ResolvedList | null; ambiguous: string[] | null } {
  const topic = RESULT_TOPIC.disposition;
  const cands: Array<{ f: Sourced; options: AuthOption[] }> = [];
  for (const f of conventions(analysis)) {
    if (!topic.test(aboutText(f))) continue;
    const pairs = factPairs(f);
    const list = pairs ? null : (f.field && topic.test(f.field) ? parseLabelList(f.text) : anchoredLabelList(f.text));
    const options = pairs ?? (list ? numbered(list) : null);
    if (options) cands.push({ f, options });
  }
  if (cands.length === 0) return { chosen: null, ambiguous: null };
  const pick = (c: { f: Sourced; options: AuthOption[] }): ResolvedList => ({ options: c.options, layer: "protocol", source: sectionOf([c.f]) });
  if (new Set(cands.map((c) => listKey(c.options))).size === 1) return { chosen: pick(cands[0]), ambiguous: null };
  const want = v ? scopeOf(`${v.questionText ?? ""}`, String(v.varName ?? "")) : null;
  if (want) {
    const mine = cands.filter((c) => scopeOf(`${c.f.field ?? ""} ${aboutOf(c.f)}`) === want);
    if (mine.length > 0 && new Set(mine.map((c) => listKey(c.options))).size === 1) return { chosen: pick(mine[0]), ambiguous: null };
  }
  const sections = [...new Set(cands.map((c) => sectionOf([c.f])))];
  return { chosen: null, ambiguous: sections };
}

/** The sections of two or more DIFFERENT discontinuation lists when the question does not say which applies, else null. */
export function ambiguousDispositionSections(analysis: Record<string, unknown> | null | undefined, v?: AuthVariable): string[] | null {
  return dispositionLists(analysis, v).ambiguous;
}

/** A coded list for a topic ("pregnancy test result", "urine dipstick") out of the coding conventions. */
function topicFromProtocol(analysis: Record<string, unknown> | null | undefined, topic: Topic, skipYesNo = false, answersFit?: (labels: string[]) => boolean): ResolvedList | null {
  const facts = conventions(analysis).filter((f) => topic.test(aboutText(f)) && !(skipYesNo && isYesNoOnly(f)));
  const fits = (labels: string[]) => !answersFit || answersFit(labels);
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs && fits(pairs.map((o) => o.label))) return { options: pairs, layer: "protocol", source: sectionOf([f]) };
  }
  for (const f of facts) {
    const list = f.field && topic.test(f.field) ? parseLabelList(f.text) : anchoredLabelList(f.text);
    if (list && fits(list)) return { options: numbered(list), layer: "protocol", source: sectionOf([f]) };
  }
  return null;
}

/**
 * A pregnancy test RESULT list has result answers: a positive or negative word, "pregnant" / "not pregnant", or an
 * indeterminate one. A list whose answers are reasons for leaving ("Completed; Pregnancy; Death") only MENTIONS
 * pregnancy: its topic is the sentence around it, and the answers say what it is (checker, 2026-10-30).
 */
const RESULT_ANSWER = /\b(?:positive|negative|pos|neg|nd|reactive|non-?reactive|indeterminate|inconclusive|equivocal|invalid|pregnant|not pregnant)\b/i;
export const looksLikePregnancyResult = (labels: string[]): boolean => labels.some((l) => RESULT_ANSWER.test(l));
const DISCONTINUATION_ANSWER = /^(?:completed|adverse event|withdrawal of consent|withdrew consent|lost to follow-?up|death|investigator decision|physician decision|sponsor decision|protocol deviation|study terminated.*)$/i;
/** A pregnancy OUTCOME list is not a list of reasons for leaving the study (three or more of those answers). */
const notReasonsForLeaving = (labels: string[]): boolean => labels.filter((l) => DISCONTINUATION_ANSWER.test(l.trim())).length < 3;

/**
 * True when `options` is, label for label, a list the protocol states for ANOTHER topic (a convention that is not
 * about `topic`). The discontinuation list on a pregnancy question is the case measured: it was put there by the
 * old reading, is stored on the form, and must not be mistaken for the model's own list when the form is read again.
 */
export function isListOfAnotherTopic(analysis: Record<string, unknown> | null | undefined, options: Array<{ label: string }> | undefined, topic: Topic): boolean {
  const mine = (options ?? []).map((o) => o.label.trim().toLowerCase()).join("|");
  if (!mine) return false;
  return conventions(analysis).some((f) => {
    if (topic.test(aboutText(f))) return false;
    const pairs = factPairs(f);
    return !!pairs && pairs.map((o) => o.label.trim().toLowerCase()).join("|") === mine;
  });
}

/** The field a convention is about: its stored field name, else the words before the first colon or equals sign. */
export const conventionField = (f: Sourced): string => f.field ?? f.text.split(/[:=]/)[0];

/** Where in the conventions each governed concept is named (matched on the field, never on the label text). */
const FIELD_TOPIC: Partial<Record<AuthorityConcept, RegExp>> = {
  race: /\brace\b|racial/i,
  ethnicity: /ethnic|hispanic|latino/i,
  action: /(?:study[- ]?drug|treatment|drug|dose)\s+action|action\s+(?:taken|with)/i,
  sex: /\bsex\b|\bgender\b/i,
  outcome: /\boutcome\b/i,
};

/** What an action field is about: "device", "other", "drug" (drug, IMP, study treatment or the study's own product), or null. */
export function actionKind(text: string, analysis: Record<string, unknown> | null | undefined, genericProductIsDrug = true): "device" | "other" | "drug" | null {
  const t = text.toLowerCase();
  if (/\bdevices?\b/.test(t)) return "device";
  if (/\bother\b/.test(t)) return "other";
  // The study's own product, however the protocol names it ("study product" is what a protocol that avoids the word
  // "drug" writes: HVU-SLP-07, 2026-10-30). A device or "other" is decided above, so these never reach a device study's field.
  if (/\b(?:study[- ]?(?:drugs?|treatments?)|drugs?|imp|investigational (?:medicinal )?products?|treatment|dose)\b/.test(t)) return "drug";
  // ...and a generic word for the product ("study product", "study intervention", "investigational agent") means the
  // DRUG only when the study tests a drug: on a device or no-product study it means the device or the intervention,
  // and the device / other handling in crfAuthority.ts must keep deciding those fields (2026-10-30, measured on a DBS study).
  if (genericProductIsDrug && /\b(?:study[- ]?(?:products?|medications?|medicines?|interventions?|agents?|capsules?|tablets?|therapy)|investigational (?:agents?|drugs?))\b/.test(t)) return "drug";
  const names = factsOfNames(analysis);
  return names.some((n) => t.includes(n)) ? "drug" : null;
}

/** The study's own intervention names, lower case (4+ characters, so an initial never matches). */
function factsOfNames(analysis: Record<string, unknown> | null | undefined): string[] {
  const raw = studyInterventions<unknown>(analysis);
  if (!Array.isArray(raw)) return [];
  return raw.map((i) => String(typeof i === "string" ? i : (i as { name?: unknown })?.name ?? "").toLowerCase().replace(/\s*\(.*$/, "").trim()).filter((n) => n.length >= 4);
}

/**
 * An action convention applies to a variable only when both name the same thing (drug with drug, device with
 * device, other with other). A variable that names something the convention does not, or nothing while the
 * convention names a product, keeps the model's list. With no variable to compare, any action convention applies.
 */
function sameProduct(concept: AuthorityConcept, f: Sourced, analysis: Record<string, unknown> | null | undefined, v?: AuthVariable, productType?: string | null): boolean {
  if (concept !== "action" || !v) return true;
  const generic = productType !== "device" && productType !== "other";
  return actionKind(conventionField(f), analysis, generic) === actionKind(`${v.questionText ?? ""} ${v.varName ?? ""}`, analysis, generic);
}

/** A coded list for a concept, from the coding conventions whose FIELD names the concept. */
export function fieldTopicFromProtocol(analysis: Record<string, unknown> | null | undefined, concept: AuthorityConcept, v?: AuthVariable, productType?: string | null): ResolvedList | null {
  const topic = FIELD_TOPIC[concept];
  if (!topic) return null;
  const facts = conventions(analysis).filter((f) => topic.test(conventionField(f)) && sameProduct(concept, f, analysis, v, productType));
  for (const f of facts) {
    const pairs = factPairs(f);
    if (pairs) return { options: pairs, layer: "protocol", source: sectionOf([f]) };
  }
  for (const f of facts) {
    const list = f.field && topic.test(f.field) ? parseLabelList(f.text) : anchoredLabelList(f.text);
    if (list) return { options: numbered(list), layer: "protocol", source: sectionOf([f]) };
  }
  return null;
}

/** "Race (collected at some sites only [NAT]: alfa, beta and gama)": the list after the last colon, or null. */
function listAfterColon(text: string): string[] | null {
  const idx = text.lastIndexOf(":");
  // Two lists in one statement (one per country or site group) cannot be told apart here: no list.
  if (idx < 0 || text.indexOf(":") !== idx) return null;
  const tail = text.slice(idx + 1).replace(/[)\]\s.]+$/, "");
  if (!/[,;]|\band\b|\bor\b/i.test(tail)) return null;
  // The same separator rules as parseLabelList: "Black or African American" is one answer, not two.
  const items = checkedAnswers(splitAnswers(tail, true), false);
  if (!items || items.some((i) => i.split(/\s+/).length > 5)) return null;
  return items;
}

/** A demographic item the protocol collects with its categories listed ("Race (...: a, b and c)"). */
function demographicsList(analysis: Record<string, unknown> | null | undefined, topic: RegExp): ResolvedList | null {
  for (const f of factsOf(analysis, "demographicsFields")) {
    if (!topic.test(f.text.split(/[(:]/)[0])) continue;
    const list = listAfterColon(f.text);
    // A table of counts ("12 Asian, 5 White, 3 Black") is not a list of categories: half or more of the items carry a number.
    if (list && list.filter((i) => /\d/.test(i)).length * 2 >= list.length) continue;
    if (list) return { options: numbered(list), layer: "protocol", source: sectionOf([f]) };
  }
  return null;
}

/**
 * The protocol names a list for this concept but it could not be read with confidence: the text of that
 * statement, so the caller keeps the model's list (and says so) instead of substituting CDASH.
 */
export function unreadableProtocolList(concept: AuthorityConcept, analysis: Record<string, unknown> | null | undefined, v?: AuthVariable, productType?: string | null): string | null {
  if (protocolList(concept, analysis, v, productType)) return null;
  const topic = FIELD_TOPIC[concept];
  if (topic) {
    const f = conventions(analysis).find((x) => topic.test(conventionField(x)) && sameProduct(concept, x, analysis, v, productType));
    if (f) return sectionOf([f]);
    if (concept === "race" || concept === "ethnicity") {
      // A category list after a colon; a table of counts ("12 Asian, 5 White") is not one.
      const listLike = (t: string) => {
        const tail = t.slice(t.lastIndexOf(":") + 1);
        const items = tail.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
        return /[,;]|\band\b|\bor\b/i.test(tail) && !(items.length > 0 && items.filter((s) => /\d/.test(s)).length * 2 >= items.length);
      };
      const d = factsOf(analysis, "demographicsFields").find((x) => topic.test(x.text.split(/[(:]/)[0]) && /:/.test(x.text) && listLike(x.text));
      if (d) return sectionOf([d]);
    }
    return null;
  }
  // A pregnancy or dipstick convention whose FIELD names the result and whose text states an answer list the reader
  // could not read. A statement that only mentions the test ("Pregnancy test (serum or urine) at screening",
  // "including pregnancy test dates", "Urinalysis by dipstick at screening, week 4 and week 12") is not one.
  if (concept === "pregnancyResult" || concept === "pregnancyOutcome" || concept === "dipstickResult" || concept === "disposition") {
    const f = conventions(analysis).find((x) => {
      const field = x.field ?? (/:/.test(x.text) ? x.text.split(":")[0] : "");
      if (concept === "pregnancyResult") {
        // A list that reads and has no result answers is not "a result list that could not be read".
        const labs = factPairs(x)?.map((o) => o.label) ?? anchoredLabelList(x.text) ?? parseLabelList(x.text);
        if (labs && !looksLikePregnancyResult(labs)) return false;
      }
      return RESULT_TOPIC[concept].test(field) && statesAnswerList(x) && !((concept === "pregnancyResult" || concept === "pregnancyOutcome") && isYesNoOnly(x));
    });
    if (concept === "disposition") {
      const amb = ambiguousDispositionSections(analysis, v);
      if (amb) return amb.join(" and ");
    }
    return f ? sectionOf([f]) : null;
  }
  const key = concept === "severity" ? "aeSeverityScale" : concept === "causality" ? "aeCausalityScale" : null;
  if (key) {
    // A bare heading ("Adverse Event Causality Definition") is not a stated list; two or more facts, or separators in one, are.
    const facts = factsOf(analysis, key);
    const f = facts.find(statesAnswerList) ?? (facts.length >= 2 ? facts[0] : undefined);
    if (f) return sectionOf([f]);
  }
  return null;
}

/** A causality statement worded as one question about a "reasonable possibility" (ICH E2A / FDA IND safety wording). */
const REASONABLE_POSSIBILITY = /\breasonable\s+(?:causal\s+)?possibility\b/i;
/** ...that also gives the second answer: "versus no reasonable possibility", "/ no reasonable possibility", "yes/no", "yes or no". */
const SECOND_ANSWER = /\b(?:no|not\s+a?|without\s+a?)\s+reasonable\s+(?:causal\s+)?possibility\b|\byes\s*(?:\/|or)\s*no\b/i;

const GRADED_LABEL = /\b(?:possibly|probably|definitely|unlikely|certainly|doubtful)\b/i;

/** Two or more distinct causality level words in one statement: a scale, not a bare yes/no question. */
function hasLevelWords(text: string): boolean {
  const words = text.toLowerCase().match(/\b(?:not related|unrelated|possibly related|probably related|definitely related|unlikely|possible|probable|definite|certain|related)\b/g) ?? [];
  return new Set(words).size >= 2;
}

/** The statement without the sentences that only ask the reasonable-possibility yes/no question. */
function withoutPossibilityQuestion(text: string): string {
  return text
    .split(/(?<=[.?!])\s+/)
    .filter((sent) => !(REASONABLE_POSSIBILITY.test(sent) && !hasLevelWords(sent) && !/^[A-Z][A-Za-z -]{1,40}:/.test(sent)) && !/\byes\s*(?:\/|or)\s*no\b/i.test(sent))
    .join(" ");
}

/** "Related: a. Possibly related: b. Not related: c.": each level introduced by its label and a colon. */
function colonLevels(text: string): string[] | null {
  const parts = text.split(/(?<=[.;])\s+(?=[A-Z][A-Za-z-]*(?: [A-Za-z-]+){0,3}:\s)/);
  if (parts.length < 2) return null;
  // Sentences such as "Note: ...", "Definition: ..." or "Reference: ICH E2A." are not levels: only segments whose label
  // is a causality level count, and at least two are needed.
  const labels = parts.map((p) => /^([^:]{2,40}):/.exec(p)?.[1]?.trim() ?? "").filter((l) => l && CAUSALITY_LABEL.test(l));
  if (labels.length < 2) return null;
  return new Set(labels.map((l) => l.toLowerCase())).size === labels.length ? labels.map(cap) : null;
}

/**
 * True when a convention states an answer list: code pairs, or separators outside brackets in its answer part (the
 * text after "<field>:" when the field is written into the text).
 */
function statesAnswerList(f: Sourced): boolean {
  if (factPairs(f)) return true;
  const answers = f.field ? f.text : f.text.slice(f.text.indexOf(":") + 1);
  if (/[,;/]|\sor\s/i.test(stripBrackets(answers))) return true;
  // "Pregnancy test result (negative, positive, not done)": a list in a bracket that closes the statement is not read
  // (a bracket is never the list), but it IS a list the protocol states, so the field says "please check".
  const closing = /\(([^()[\]]*)\)\s*\.?\s*$/.exec(answers);
  return !!closing && /[,;/]/.test(closing[1]);
}

/** What the protocol states for a concept, or null. */
export function protocolList(concept: AuthorityConcept, analysis: Record<string, unknown> | null | undefined, v?: AuthVariable, productType?: string | null): ResolvedList | null {
  switch (concept) {
    case "severity": {
      const facts = factsOf(analysis, "aeSeverityScale");
      // Review item 18 (7), decision G: a statement that says its words correspond to Grades 1-n labels the
      // same list "Grade 1 (Mild)" ... with the same codes. Only a list that IS those words, coded 1..n.
      const gradedFact = facts.find((f) => gradedSeverityWords(f.text));
      const graded = gradedFact ? gradedSeverityWords(gradedFact.text)! : null;
      const asGrades = (options: AuthOption[]): AuthOption[] =>
        graded && options.length === graded.length && options.every((o, i) => String(o.code) === String(i + 1) && o.label.toLowerCase().replace(/\s/, "-") === graded[i].toLowerCase())
          ? options.map((o, i) => ({ code: o.code, label: `Grade ${i + 1} (${o.label})` }))
          : options;
      const s = scaleFromFacts(facts, (l) => SEVERITY_LABEL.test(l)) ?? gradeLevelsFromFacts(facts);
      if (s) return { options: asGrades(s.options), layer: "protocol", source: sectionOf(s.facts) };
      // The protocol NAMES a published grading scale instead of listing its levels: the grades are the standard's own.
      // A list the protocol prints in a sentence of the statement ("mild, moderate or severe") wins over a scale
      // that the same statement names for something else (labs, a negation).
      for (const f of facts) {
        const words = printedSeverityInStatement(f.text);
        if (words) return { options: asGrades(numbered(words)), layer: "protocol", source: sectionOf([f]) };
      }
      // The words printed with their grade range ("... or fatal (corresponding to Grade 1 to 5)").
      if (graded && gradedFact) return { options: asGrades(numbered(graded)), layer: "protocol", source: sectionOf([gradedFact]) };
      for (const f of facts) {
        const named = namedSeverityScale(f.text);
        if (named) return { options: named.options, layer: "protocol", source: `${sectionOf([f])}; ${named.label} grades${named.multipleVersions ? " (the protocol names more than one version; none recorded)" : ""}` };
      }
      // No severity scale in the reading, but a coding convention whose FIELD is the severity (HVU's "Severity" row
      // of the coded-answers table): its codes, with the short level word as the label.
      for (const f of conventions(analysis)) {
        if (!/\b(?:severity|intensity)\b/i.test(conventionField(f))) continue;
        const pairs = factPairs(f);
        const levels = pairs ? shortLevels(pairs, (l) => SEVERITY_LABEL.test(l)) : null;
        if (levels && levels.every((o) => SEVERITY_LABEL.test(o.label) || /^grade\b/i.test(o.label))) return { options: levels, layer: "protocol", source: sectionOf([f]) };
        const list = f.field ? parseLabelList(f.text) : null;
        if (list && list.every((l) => SEVERITY_LABEL.test(l))) return { options: numbered(list), layer: "protocol", source: sectionOf([f]) };
      }
      return null;
    }
    case "causality": {
      const all = factsOf(analysis, "aeCausalityScale");
      // LEVELS FIRST. A scale with named levels stays that scale even when the protocol also says a
      // "reasonable possibility" (the yes/no question it asks for expedited reporting, or the wording of a level).
      // Sentences about the reasonable-possibility question are not part of the level list.
      const levelFacts: Sourced[] = all
        .filter((f) => hasLevelWords(f.text) || !(REASONABLE_POSSIBILITY.test(f.text) && SECOND_ANSWER.test(f.text)))
        .map((f) => ({ ...f, text: withoutPossibilityQuestion(f.text) }))
        .filter((f) => f.text.trim() !== "");
      const colon = all.map((f) => ({ f, levels: colonLevels(f.text) })).find((x) => x.levels);
      if (colon) return { options: numbered(colon.levels!), layer: "protocol", source: sectionOf([colon.f]) };
      // One fact per level, each opening with its label and a dash or colon ("Related - The AE is known to occur...",
      // "Not Related - There is not a reasonable possibility..."): the label list is the scale, whatever the definitions say.
      const opening = all.map((f) => {
        const m = /^\s*([A-Za-z][A-Za-z -]{1,38}?)\s*(?:[–—:]|\s-\s)\s*\S/.exec(f.text);
        return m && CAUSALITY_LABEL.test(m[1]) ? { f, label: cap(m[1].trim().toLowerCase()) } : null;
      });
      if (opening.length >= 2 && opening.every((x) => x) && new Set(opening.map((x) => x!.label)).size === opening.length) {
        return { options: numbered(opening.map((x) => x!.label)), layer: "protocol", source: sectionOf(opening.map((x) => x!.f)) };
      }
      // A graded label (possibly / probably / unlikely / definitely) anywhere means this is not a related / not related pair.
      const graded = levelFacts.some((f) => GRADED_LABEL.test(f.text));
      const twoWay = graded ? undefined : levelFacts.find((f) => /\brelated\s*(?:\/|or|,)\s*not related\b/i.test(f.text));
      if (twoWay) return { options: numbered(["Related", "Not related"]), layer: "protocol", source: sectionOf([twoWay]) };
      const s = scaleFromFacts(levelFacts, (l) => CAUSALITY_LABEL.test(l));
      if (s) return { options: s.options, layer: "protocol", source: sectionOf(s.facts) };
      // "related or not related to the study drug": two categories, however the sentence is worded.
      const binary = graded ? undefined : levelFacts.find((f) => /\brelated\b/i.test(f.text) && /\bnot related\b|\bunrelated\b/i.test(f.text));
      if (binary) return { options: numbered(["Related", "Not related"]), layer: "protocol", source: sectionOf([binary]) };
      // No level labels anywhere: "Is there a reasonable possibility that the study drug caused the event? Yes/No"
      // is ONE yes/no question, on the study's own yes/no codes (its convention, else the platform's 1/0).
      const possibility = all.find((f) => REASONABLE_POSSIBILITY.test(f.text) && SECOND_ANSWER.test(f.text));
      if (possibility) {
        const yn = yesNoFromProtocol(analysis);
        const yes = yn?.options.find((o) => isYes(o.label));
        const no = yn?.options.find((o) => isNo(o.label));
        return { options: [{ code: yes?.code ?? 1, label: "Yes" }, { code: no?.code ?? 0, label: "No" }], layer: "protocol", source: sectionOf([possibility]) };
      }
      return null;
    }
    case "doseLevel": return buildDoseLevels(analysis);
    case "outcome": return topicFromProtocol(analysis, RESULT_TOPIC.outcome);
    case "sex": return sexFromProtocol(analysis);
    case "yesno": return yesNoFromProtocol(analysis);
    case "pregnancyResult": return topicFromProtocol(analysis, RESULT_TOPIC.pregnancyResult, true, looksLikePregnancyResult);
    case "pregnancyOutcome": return topicFromProtocol(analysis, RESULT_TOPIC.pregnancyOutcome, true, (l) => notReasonsForLeaving(l) && !isListOfAnotherTopic(analysis, l.map((label) => ({ label })), RESULT_TOPIC.pregnancyOutcome));
    case "disposition": return dispositionLists(analysis, v).chosen;
    case "dipstickResult": return topicFromProtocol(analysis, RESULT_TOPIC.dipstickResult);
    case "race": return fieldTopicFromProtocol(analysis, "race") ?? demographicsList(analysis, /\brace\b|racial/i);
    case "ethnicity": return fieldTopicFromProtocol(analysis, "ethnicity") ?? demographicsList(analysis, /ethnic|hispanic|latino/i);
    case "action": return fieldTopicFromProtocol(analysis, "action", v, productType);
    default: return null;
  }
}

// ── Demographic items the protocol does not collect, or lists once per country ──

const DEMO_TOPIC = { race: /\brace\b|racial/i, ethnicity: /ethnic|hispanic|latino/i } as const;
const HEDGE = /\bunless\b|\bexcept\b|beyond what is required|\bwhere\s+(?:required|permitted)|\bif\s+required|\bas required\b/i;
const NO_ITEM = /\bno\s+(?:\w+\s+){0,4}(?:question|field|item|data)s?\b/i;
const NOT_COLLECTED = /\bnot\s+(?:be\s+)?(?:collect|record|captur|ask|obtain|report)/i;
/**
 * "Unknown or Not Reported" (or a bare "Not reported") as an ANSWER in a category list is not a statement that the
 * item is not reported (backlog 21 (bbb): the first build of a real protocol dropped race because its race list
 * ends "..., White, Other, Unknown or Not Reported"). Only a label introduced by a list separator counts;
 * "Race is not reported." stays a negation.
 */
const ANSWER_NOT_REPORTED = /(?:^|[,;:/(]|\bor\b|\band\b)\s*(?:unknown\s*(?:or|\/|and)\s*)?not\s+reported\b(?=\s*(?:[,;.)/]|$))/gi;
const saysNotCollected = (text: string): boolean => NOT_COLLECTED.test(text.replace(ANSWER_NOT_REPORTED, " "));
const WHOLE_STUDY = /\b(?:either|both|any|all|every)\s+(?:of\s+the\s+)?(?:countr|sites?)|\banywhere\b|\bat all\b|\bin (?:this|the) study\b/i;
// A statement limited to one place ("in Japan", "Japanese forms", "for the UK sites", "in Brazil only").
// A negation limited to a subgroup, a visit, a form or a format, or followed by "but ... collected", is not study-wide either.
const LIMITED = /\bfor\s+(?:the\s+)?(?:[A-Za-z-]+\s+){0,2}(?:participants?|subjects?|patients?|children|adults|adolescents|cohorts?|subgroups?|arms?|groups?|females?|males?|women|men)\b|\bat\s+(?:the\s+)?(?:[A-Za-z0-9-]+\s+){0,3}(?:visits?|screening|baseline|follow-up)\b|\b(?:on|in)\s+the\s+(?:[A-Za-z-]+\s+){0,3}(?:forms?|pages?|CRFs?)\b|\bas\s+free[- ]text\b|\bbut\b[^.]*\bcollected\b/i;
const ONE_PLACE = /\bin\s+[A-Z][A-Za-z]+|\b[A-Z][a-z]+(?:ese|ian|ish)\s+(?:sites?|forms?|participants?)|\bfor\s+(?:the\s+)?[A-Z][A-Za-z]*\s+sites?\b|\bonly\b/;

function sentencesOf(text: string): string[] {
  return text.split(/(?<=[.!?;])\s+/).map((x) => x.trim()).filter(Boolean);
}

/**
 * A numbered heading line ("11. Demographic and Baseline Data", "8.2 Demographics"): short, no sentence
 * punctuation at the end, and never a date or a dot-leader TOC line (the veto shared with the citation index,
 * crf/protocolHeadings.isNeverAHeadingLine; until 2026-10-19 "09 Aug 2021" labelled the paragraphs under it
 * "protocol section 09 Aug 2021").
 */
const NUMBERED_HEADING = /^\s*(\d+(?:\.\d+)*)\.?\s+([A-Z][^\n]{0,90})$/;
const headingOf = (line: string): { depth: number; title: string; label: string } | null => {
  const t = line.trim();
  const m = NUMBERED_HEADING.exec(t);
  if (!m || /[.:;,]$/.test(t) || isNeverAHeadingLine(t)) return null;
  return { depth: m[1].split(".").length, title: m[2], label: t };
};

/**
 * The paragraphs of the full protocol text that can speak about demographics, each with the numbered heading
 * above it: the demographics section when the text has one (a numbered heading that names demographics, up
 * to the next heading as shallow or shallower), otherwise every paragraph.
 */
export function demographicParagraphs(protocolText: string | null | undefined): Array<{ text: string; where: string }> {
  const lines = String(protocolText ?? "").split(/\r?\n/);
  if (lines.every((l) => !l.trim())) return [];
  // The table of contents is neither a heading nor a paragraph: its demographics entry is not the section, and
  // its lines are not the protocol saying anything (crf/protocolHeadings, shared with the citation index).
  const toc = tocLineFlags(lines);
  const headingAt = (i: number) => (toc[i] ? null : headingOf(lines[i]));
  const nearest: string[] = [];
  let current = "";
  for (let i = 0; i < lines.length; i++) { const h = headingAt(i); if (h) current = h.label; nearest.push(current); }
  let from = -1;
  let to = lines.length;
  for (let i = 0; i < lines.length; i++) {
    const h = headingAt(i);
    if (!h || !/demograph/i.test(h.title)) continue;
    from = i;
    for (let j = i + 1; j < lines.length; j++) {
      const n = headingAt(j);
      if (n && n.depth <= h.depth) { to = j; break; }
    }
    break;
  }
  const out: Array<{ text: string; where: string }> = [];
  for (let i = from < 0 ? 0 : from; i < (from < 0 ? lines.length : to); i++) {
    if (!lines[i].trim() || toc[i] || LEADER_LINE.test(lines[i].trim()) || headingAt(i)) continue;
    out.push({ text: lines[i].trim(), where: nearest[i] ? `protocol section ${nearest[i].replace(/^(\d+(?:\.\d+)*)\.?\s+/, "$1 ")}` : "protocol text" });
  }
  return out;
}

/**
 * Where the protocol says a demographic item (race or ethnicity) is NOT collected in the study, or null.
 * Only a statement about the whole study counts ("in either country", or no place named), and only when no
 * other sentence collects the item. A statement limited to one country or site group does not.
 *
 * The protocol reading is read first. When it says nothing about the item, the FULL protocol text is read
 * (its demographics section, else whole sentences) with the same rules, because the reading can leave out
 * a sentence the protocol states.
 */
export function demographicNotCollected(analysis: Record<string, unknown> | null | undefined, topic: "race" | "ethnicity", protocolText?: string | null): string | null {
  const re = DEMO_TOPIC[topic];
  let negated: Sourced | null = null;
  for (const f of factsOf(analysis, "demographicsFields")) {
    for (const sentence of sentencesOf(f.text)) {
      if (!re.test(sentence)) continue;
      // A conditional statement ("unless required", "where permitted") is neither a study-wide negation nor a collection.
      if (HEDGE.test(sentence)) continue;
      if (!saysNotCollected(sentence) && !NO_ITEM.test(sentence)) {
        // A bare item name or a sentence that collects it: the protocol collects it.
        return null;
      }
      if (!LIMITED.test(sentence) && (WHOLE_STUDY.test(sentence) || !ONE_PLACE.test(sentence))) negated = negated ?? f;
    }
  }
  if (negated) return sectionOf([negated]);
  // The reading did not collect the item and did not negate it for the whole study (it said nothing, hedged,
  // or limited the statement to one place): the full text decides.
  if (!protocolText) return null;
  let found: string | null = null;
  for (const para of demographicParagraphs(protocolText)) {
    for (const sentence of sentencesOf(para.text)) {
      if (!re.test(sentence) || HEDGE.test(sentence)) continue;
      if (!saysNotCollected(sentence) && !NO_ITEM.test(sentence)) return null;
      if (!LIMITED.test(sentence) && (WHOLE_STUDY.test(sentence) || !ONE_PLACE.test(sentence))) found = found ?? para.where;
    }
  }
  return found;
}

const PLACE_GROUP = /\b(?:for|in)\s+(?:the\s+)?([A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*)*)\s+(?:sites?|centres?|centers?|countr(?:y|ies))\b/g;
/** "Canadian categories:", "UK ONS categories:": a named list per place, introduced by a colon. */
const CATEGORY_LIST_HEAD = /\b((?:[A-Z][A-Za-z]*\s+){0,3}[A-Z][A-Za-z]*)\s+(?:[a-z]+\s+){0,2}(?:categories|classification|list)\s*:/g;
/** Country names and demonyms (a place label must name one), plus UK, US and EU. */
const COUNTRY_WORDS = new Set((
  "uk us usa eu united kingdom britain british english scottish welsh irish america american canada canadian mexico mexican brazil brazilian argentina argentine chile chilean colombia colombian peru peruvian " +
  "france french germany german italy italian spain spanish portugal portuguese netherlands dutch belgium belgian switzerland swiss austria austrian sweden swedish norway norwegian denmark danish finland finnish poland polish " +
  "russia russian ukraine ukrainian turkey turkish greece greek israel israeli egypt egyptian nigeria nigerian kenya kenyan ghana ghanaian ethiopia ethiopian uganda ugandan tanzania tanzanian zambia zambian zimbabwe zimbabwean " +
  "africa african south india indian pakistan pakistani bangladesh bangladeshi china chinese japan japanese korea korean taiwan taiwanese vietnam vietnamese thailand thai philippines filipino indonesia indonesian malaysia malaysian singapore " +
  "australia australian zealand new saudi arabia arabian iran iranian iraq iraqi australasia european"
).split(/\s+/));
const namesCountry = (label: string): boolean => label.toLowerCase().split(/[^a-z]+/).some((w) => COUNTRY_WORDS.has(w));
/** A named classification source: an adjective before "census", or a statistics office or agency. */
const SOURCE_NAME = [
  /\b(?:the\s+)?([A-Za-z]+)\s+census\b/gi,
  /\b(ONS|IBGE|INSEE|ISTAT|DESTATIS|StatCan)\b/g,
  /\b(?:[A-Z][A-Za-z]*\s+)?(?:Office|Institute|Bureau|Agency|Department|Ministry|Council)\s+(?:for|of)\s+(?:National\s+)?(?:[A-Z][A-Za-z]*\s+(?:and\s+)?){0,4}Statistics\b/g,
];
const SOURCE_STOP = /^(?:the|a|an|national|population|housing|each|every|this|that|of|per)$/i;

const COUNTRY_ALIASES: string[][] = [
  ["uk", "britain", "british", "united kingdom", "english", "scottish", "welsh", "ons"],
  ["us", "usa", "america", "american", "omb"],
  ["canada", "canadian", "statcan"], ["brazil", "brazilian", "ibge"], ["france", "french", "insee"],
  ["italy", "italian", "istat"], ["germany", "german", "destatis"],
];
/** The country a place label stands for ("UK ONS" and "British census" are the UK; "US OMB" is the US). */
function countryKey(label: string): string {
  const words = label.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  for (const w of words) {
    const g = COUNTRY_ALIASES.find((a) => a.includes(w));
    if (g) return g[0];
    if (COUNTRY_WORDS.has(w)) return w;
  }
  return label.toLowerCase();
}
/** One name per country, in order: two lists from the same country are not two places. */
function distinctCountries(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of names) { const k = countryKey(n); if (!seen.has(k)) { seen.add(k); out.push(n); } }
  return out;
}

/** The distinct places or sources a statement gives separate lists for. */
function placesIn(text: string, includePlaceGroups: boolean): string[] {
  const names = new Map<string, string>();
  const add = (n: string) => { const k = n.trim().toLowerCase(); if (k && !names.has(k)) names.set(k, n.trim()); };
  if (includePlaceGroups) for (const m of text.matchAll(PLACE_GROUP)) add(m[1]);
  for (const m of text.matchAll(CATEGORY_LIST_HEAD)) {
    const name = m[1].trim();
    if (namesCountry(name)) add(name);
  }
  // Two named classification sources in one statement (one census, one statistics office).
  const sources = new Map<string, string>();
  for (const re of SOURCE_NAME) {
    for (const m of text.matchAll(re)) {
      const name = (m[1] ?? m[0]).replace(/^the\s+/i, "").trim();
      // A source counts when a country is named with it: a national statistics acronym, or a country adjective before "census" or a statistics office.
      const national = /^(?:ONS|IBGE|INSEE|ISTAT|DESTATIS|StatCan)$/.test(name);
      if (!SOURCE_STOP.test(name) && (national || namesCountry(name))) sources.set(name.toLowerCase(), name);
    }
  }
  if (sources.size >= 2) for (const n of sources.values()) add(n);
  return [...names.values()];
}

/**
 * The protocol gives a separate list of categories per country or site group for this item (two or more
 * places named in the statements about it), or null. Returns a short description for the field note.
 *
 * Detected: "for the X sites ... for the Y sites"; "<Place> categories: ... <Other place> categories: ...";
 * two named classification sources in one statement (census, a statistics office). The stored reading is read
 * first, then the full protocol text (demographics section, else every paragraph that names the item).
 */
export function perCountryDemographicList(analysis: Record<string, unknown> | null | undefined, topic: "race" | "ethnicity", protocolText?: string | null): string | null {
  const re = DEMO_TOPIC[topic];
  // Places named across ALL the statements about the item (one statement per country is common).
  const places = new Map<string, string>();
  let first: Sourced | null = null;
  // Separate facts that each give ONE place's list for the item (one fact per country) count together.
  const oneEach = new Map<string, string>();
  for (const f of factsOf(analysis, "demographicsFields")) {
    if (!re.test(f.text.split(/[(:]/)[0]) || !/:/.test(f.text)) continue;
    if (saysNotCollected(f.text)) continue;
    const head = f.text.split(/:/)[0];
    for (const p of [...placesIn(head, true), ...placesIn(f.text, false)]) {
      if (!places.has(p.toLowerCase())) places.set(p.toLowerCase(), p);
      first = first ?? f;
    }
    // A fact that names one classification source of its own ("Canadian census ...: a; b", "UK ONS ...: x; y").
    for (const re2 of SOURCE_NAME) for (const m of head.matchAll(re2)) {
      const name = (m[1] ?? m[0]).replace(/^the\s+/i, "").trim();
      const national = /^(?:ONS|IBGE|INSEE|ISTAT|DESTATIS|StatCan)$/.test(name);
      if (!SOURCE_STOP.test(name) && (national || namesCountry(name))) { oneEach.set(name.toLowerCase(), name); first = first ?? f; }
    }
  }
  // A source already inside a named place ("ONS" inside "UK ONS") is the same place, not a third.
  if (oneEach.size >= 2) for (const [k, n] of oneEach) if (![...places.keys()].some((p) => p.includes(k) || k.includes(p))) places.set(k, n);
  const countries = distinctCountries([...places.values()]);
  if (countries.length >= 2 && first) return `${countries.join(" and ")} lists, ${sectionOf([first])}`;
  if (!protocolText) return null;
  for (const para of demographicParagraphs(protocolText)) {
    if (!re.test(para.text) || saysNotCollected(para.text)) continue;
    const named = distinctCountries(placesIn(para.text, true));
    if (named.length >= 2) return `${named.join(" and ")} lists, ${para.where}`;
  }
  return null;
}

/** True when the text names a device (an action field about a device takes no drug list). */
export function namesDevice(text: string): boolean {
  return /\bdevices?\b/i.test(text);
}

/** True when the list carries drug-dose wording ("dose", "drug"). */
export function hasDrugDoseWording(options: Array<{ label: string }> | undefined): boolean {
  return (options ?? []).some((o) => /\bdoses?\b|\bdrugs?\b/i.test(o.label));
}

export const isSelect = (v: AuthVariable) => v.responseType === "single_select" || v.responseType === "multi_select";

/** The concepts whose lists belong to adverse-event-family forms (pregnancy outcome shares only the outcome concept). */
const AE_CONCEPTS: ReadonlySet<AuthorityConcept> = new Set<AuthorityConcept>(["severity", "causality", "outcome", "action"]);
const AE_FAMILY_FORM = /death report|device incident|product complaint|hospitali[sz]ation|special interest|important medical|\bSUSARs?\b|\breactions?\b|overdose|treatment[- ]emergent|adverse|\bSAEs?\b|\bAEs?\b|\bAESIs?\b|\bADEs?\b|\bUADEs?\b|\bSADEs?\b|device deficienc|serious (?:event|incident)|safety event/i;
const PREGNANCY_FORM = /pregnan/i;

/**
 * Whether the AE-family concepts (outcome, severity, relationship or causality, action taken) belong on a form.
 * They describe an EVENT, so they apply only on an adverse event, serious adverse event, AESI, adverse device
 * effect or device deficiency form (pregnancy outcome only for outcome). With no form name the answer is yes
 * (callers that do not know the form keep the old behaviour).
 */
export function aeConceptAppliesOnForm(concept: AuthorityConcept, formName: string | undefined | null): boolean {
  if (!AE_CONCEPTS.has(concept)) return true;
  const f = String(formName ?? "").trim();
  if (!f) return true;
  if (AE_FAMILY_FORM.test(f)) return true;
  return concept === "outcome" && PREGNANCY_FORM.test(f);
}

/**
 * A question that asks how an event relates to the STUDY TREATMENT, on any form (Jim, 2026-10-17: the protocol's causality
 * scale governs every such question, not only those on an adverse event form). Decided by the variable's meaning:
 * On a form that is not an adverse event family form a question is governed ONLY when BOTH hold (structured signal first;
 * wording alone is never enough, Jim 2026-10-17):
 *  - the variable name is the relationship code of an event ("AEREL", "DLTREL", "DTHREL", "IRAEREL", "RELDRUG"...), and
 *  - the text asks for a relationship / cause / attribution (not the adjective in "treatment-related visits") and names
 *    nothing else the event could be related to (a procedure, a concomitant or prior medication, the disease, a person).
 * When unsure the answer is no: an ungoverned list stays the model's list, while a wrongly governed one replaces a correct
 * list with the wrong scale. On adverse event family forms the word-based classification is unchanged.
 */
const RELATION_WORDS = /\b(?:relationship|relationships|relatedness|related|relation|causal|causality|causally|caused|cause|attribut(?:ion|able|ed)|attributability)\b/i;
const OTHER_CAUSE = /\bnon[- ]study\b|\bconcomitant\b|\bprocedures?\b|\bbiops(?:y|ies)\b|\bunderlying\b|\b(?:disease|condition|illness|malignancy|tumou?r)\b|\bprior\b|\bpast\b|\bprevious\b|\bbackground\b|\bother\b|\bsignatory\b|\bparent\b|\bguardian\b|\bcaregiver\b|\brelative\b|\bfamily\b|\bto the (?:participant|subject|patient)\b|\bpremedication\b|\bvehicle\b|\bcarrier\b|\bpump\b|\bdevice\b|\binfusion set\b|\baccountability\b|\blot\b|\bbatch\b|\bchemotherapy\b|\bcomparator\b|\brescue\b|\bdose reduction\b/i;
/** Radiation is another cause unless the protocol lists it as an intervention of the study. */
const RADIATION = /\bradiation\b|\bradiotherapy\b/;
/** A study treatment set against something else: "or", "vs", "versus", "and/or", "compared with". */
const JOIN_AFTER = /^[\s,;]*(?:or|vs\.?|versus|and\/or|compared (?:with|to)|in comparison (?:with|to)|rather than|instead of|as opposed to|and not|in combination with)\b/;
const JOIN_BEFORE = /\b(?:or|vs\.?|versus|and\/or|compared (?:with|to)|in comparison (?:with|to)|rather than|instead of|as opposed to|and not|in combination with)\s+(?:(?:the|a|to)\s+)*$/;
const STUDY_TREATMENT_NAMED = /\bstudy (?:drug|treatment|medication|intervention)s?\b|\binvestigational\b|\b(?:IP|IMP)\b/i;
const REL_SUFFIX = "(?:DRUG|STDY|STUDY|TRT|TX|IP|IMP|PRD|PROD|MED|TRTM)";
/**
 * An event prefix, then REL, then up to six more letters or digits (a product code: "DLTRELG2", "AEREL211"); or REL with a
 * study-treatment suffix and no prefix ("RELDRUG").
 */
const REL_NAME = new RegExp(`^(?:(?:AE|SAE|AESI|DLT|DTH|DEATH|IRAE|IRR|IR|SUSAR|PREG|PG|WND|ADE|SADE|UADE|ISR|HSR|TOX|EVT|EVENT)REL[A-Z0-9]{0,6}|REL${REL_SUFFIX})$`);

/** The adjective in "treatment-related visits" / "study drug related visits": not a question about a relationship. */
const ADJECTIVAL_RELATED = /\b\w+-related\b|\b(?:treatment|drug|study|therapy|product|protocol|trial) related\b(?!\s+to\b)/g;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The names of the study's own interventions (analysis.interventions[].name; "Drug X (ABC-123)" is named by either part).
 * A name shorter than three characters is not a name.
 */
export function treatmentNamesOf(analysis: Record<string, unknown> | null | undefined): string[] {
  const out = new Set<string>();
  const ivs = studyInterventions<unknown>(analysis);
  for (const iv of ivs) {
    const n = typeof (iv as { name?: unknown })?.name === "string" ? (iv as { name: string }).name : "";
    for (const part of [n, ...n.split(/[()/,]/)].map((x) => x.trim())) if (part.length >= 3) out.add(part);
  }
  return [...out];
}

/**
 * True when the variable NAME is an event relationship code AND the text asks for a relationship, naming no other cause.
 * The text must also name the study treatment: explicit wording ("study drug", "investigational product"), or the exact
 * name (whole word, any case) of one of the study's own interventions. Either one together with the relationship code.
 */
export function isEventRelationshipQuestion(v: AuthVariable, treatmentNames: string[] = []): boolean {
  const name = String(v.varName || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const text = String(v.questionText || "").toLowerCase();
  if (!REL_NAME.test(name) || text.trim() === "") return false;
  // "the study team" or "radiation therapy" (when radiation is not an intervention of the study) are not the study treatment.
  const spans: Array<{ start: number; end: number; name: string }> = [];
  for (const m of text.matchAll(new RegExp(STUDY_TREATMENT_NAMED.source, "gi"))) spans.push({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length, name: "" });
  for (const t of treatmentNames) {
    for (const m of text.matchAll(new RegExp(`(?<![a-z0-9])${escapeRe(t.toLowerCase())}(?![a-z0-9])`, "g"))) spans.push({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length, name: t });
  }
  if (spans.length === 0) return false;
  // The study treatment set against another cause ("study drug or chemotherapy", "vs", "compared with") is not one question about it.
  if (spans.some((sp) => JOIN_AFTER.test(text.slice(sp.end)) || JOIN_BEFORE.test(text.slice(0, sp.start)))) return false;
  if (OTHER_CAUSE.test(text) || /\bcause of\b/.test(text)) return false;
  if (RADIATION.test(text) && !spans.some((sp) => /radiat/i.test(sp.name))) return false;
  return RELATION_WORDS.test(text.replace(ADJECTIVAL_RELATED, " "));
}

/**
 * The governed concept a variable carries, or null. Looks at the name and the text, never the options. A form name makes the
 * AE-family concepts form-aware, EXCEPT that an event-relationship question (relationship code + relationship wording) is
 * causality on any form that names itself.
 */
export function classifyVariable(v: AuthVariable, formName?: string | null, treatmentNames: string[] = []): AuthorityConcept | null {
  const concept = classifyVariableByWords(v);
  const form = String(formName ?? "").trim();
  // The words ("pregnancy ... outcome") can make a relationship question look like a result; the relationship code settles it.
  if (form && !AE_FAMILY_FORM.test(form) && isSelect(v) && isEventRelationshipQuestion(v, treatmentNames)) return "causality";
  return concept && aeConceptAppliesOnForm(concept, formName) ? concept : null;
}

const DISCONTINUATION_NAME = /^(?:EOS|EOT|DS|DC|DISC|WD|TERM)[A-Z]*REAS|^DSDECOD$/;
const REASON_ASKED = /\b(?:reasons?|why)\b/;
const ENDED_WORDS = /\b(?:discontinu\w*|withdr[ae]w\w*|terminat\w*|ended|end(?:ed)? (?:of )?(?:the )?(?:study|treatment|participation)|early (?:exit|end)|dropp?(?:ed|ing) out|left the study|stopp\w*|exit)\b/;
const NOT_AN_END_REASON = /\bprior\b|\bprevious(?:ly)?\b|\bpast\b|\bhistory\b|\bconcomitant\b|\bbefore (?:the )?(?:study|enrol\w*|screening)\b|\bdoses?\b|\bdosing\b|\bhold\b|\bheld\b|interrupt|not done|not performed|\bvisit\b|missed|delay|screen fail|deviation|\bmedication\b|\bspecify\b/;

function classifyVariableByWords(v: AuthVariable): AuthorityConcept | null {
  const name = String(v.varName || "").toUpperCase();
  const text = String(v.questionText || "").toLowerCase();
  if (v.responseType === "yesno") return "yesno";
  if (!isSelect(v)) return null;
  if (/non-study|concomitant/.test(text)) return null;
  if (/^(SEX|GENDER)$/.test(name) || /^(sex|gender)\b/.test(text)) return "sex";
  if (/^RACE/.test(name) || /^race\b/.test(text)) return "race";
  if (/^ETHNIC/.test(name) || /^ethnic/.test(text)) return "ethnicity";
  const all = `${name} ${text}`.toLowerCase();
  if (isDoseLevelQuestion(name, text)) return "doseLevel";
  // The reason a participant ended the study or the treatment (HVU-SLP-07: EOSREAS took the oncology list, because the
  // protocol's own discontinuation list had no concept to be read under). Not a reason for a dose hold, a visit or a test not done.
  if ((DISCONTINUATION_NAME.test(name) || (REASON_ASKED.test(text) && ENDED_WORDS.test(text))) && !NOT_AN_END_REASON.test(text)) return "disposition";
  // A pregnancy TEST RESULT and a pregnancy OUTCOME are different questions (PGOUTC took the test-result list).
  if ((/pregnan/.test(all) || /^PREG/.test(name)) && /result|res\b|outcome/.test(all)) {
    return /\boutcome\b/.test(text) && !/\btest/.test(text) ? "pregnancyOutcome" : "pregnancyResult";
  }
  if (/dipstick/.test(text) && /result|protein|glucose|blood|leuk|nitrite|ketone|ph\b|bilirubin/.test(text)) return "dipstickResult";
  if (/(^|[^A-Z])(AE|SAE)(SEV|GR|TOXGR)/.test(name) || /\b(severity|intensity)\b/.test(text) || /\b(event|ae|sae)\s+grade\b/.test(text)) return "severity";
  if (/^(AE|SAE)REL/.test(name) || /\b(relationship|causality|relatedness)\b/.test(text)) return "causality";
  if (/^(AEOUT|SAEOUT)/.test(name) || /\boutcome\b/.test(text)) return "outcome";
  if (/action taken|action with/.test(text)) return "action";
  if (/primary reason/.test(text) && /(ended|discontinu|withdr|stopp|exit)/.test(text) && !NOT_AN_END_REASON.test(text)) return "disposition";
  return null;
}


// ── The dose rule: a protocol that forbids a dose change states an action list by saying so ──

export const DOSE_NOT_PERMITTED = /\bno dose (?:modification|reduction|adjustment|titration|change)s?\b|\bno dose (?:escalation|increase)s?,?\s+(?:(?:or|and)\s+)?(?:dose )?(?:modification|reduction|adjustment|titration|change)s?\b|dose (?:modification|reduction|adjustment|change)s? (?:is|are|will be) not (?:permitted|allowed)|dose (?:modification|reduction|adjustment|change)s? (?:is|are) prohibited|not (?:permitted|allowed) to (?:reduce|modify|adjust|change) (?:the )?dose|(?:only|limited to) (?:a )?(?:dose |treatment )?(?:interruption|interrupt\w*|temporary (?:stop|interruption|discontinuation)|discontinuation)/i;
/** A denial limited to a condition ("no dose change is needed for age", "... in renal impairment") is not study-wide. */
export const DOSE_CONDITION_LIMIT = /\b(?:for|in|with|due to|because of)\s+(?:(?:the|a|an|any|patients?|participants?|subjects?|those)\s+)*(?:age\b|ages\b|aged\b|elderly|older|(?:\w+\s+){0,2}impairment|renal|hepatic|kidney|liver|body weight|weight|sex\b|gender|race\b|ethnicity|food|concomitant)/i;
/** True for a sentence that forbids a dose change for the whole study. */
export const isStudyWideDoseDenial = (sentence: string): boolean => DOSE_NOT_PERMITTED.test(sentence) && !DOSE_CONDITION_LIMIT.test(sentence);
export const DOSE_DENIAL = /\bno\b|\bnot\b|\bnever\b|prohibit|\bonly\b|\bwithout\b/i;

/** One participant is the subject of the sentence ("intra-patient", "participants who", "for toxicity"). */
const PER_PARTICIPANT = /intra-?(?:patient|subject|participant|individual)|\b(?:the|a|an|each|that|this|any|individual)\s+(?:patient|subject|participant)\b|\b(?:patients?|subjects?|participants?)\s+(?:who|with|experiencing)\b|\b(?:patient|subject|participant)(?:'s|s')\s+dose|\bfor\s+(?:toxicit|intoleran|adverse)|\bdue to\s+(?:an?\s+)?(?:toxicit|intoleran|adverse)|\bin (?:the event|case) of\s+(?:toxicit|intoleran|adverse)/i;
/** The sentence is about a cohort or the escalation scheme: which dose level the NEXT participants are enrolled at. */
const COHORT = /\bcohorts?\b|\benrol+(?:ment|ed|s|ing)?\b|\bescalation\s*(?:\/\s*de-?escalation\s*)?(?:rules?|scheme|schema|design|plan|phase|part)\b|\bnext (?:dose )?(?:level|cohort)\b|\badvance to\b|\bopen(?:ed|s)?\b(?!-label)|\bdlts?\b|dose[- ]finding|\b(?:new|subsequent|additional)\s+(?:\d+\s+)?(?:subjects|patients|participants|cohorts?)\b/i;
/** A change of a dose, in so many words. */
const DOSE_CHANGE_WORD = /reduc|increas|decreas|\blower(?:ed)?\b|de-?escalat/i;
/** The study's own treatment, in generic words. */
const STUDY_TREATMENT_WORDS = /\b(?:study (?:drug|treatment|medication|intervention|product)|investigational (?:medicinal )?product|imp|trial (?:drug|medication))\b/i;

/**
 * True when ONE sentence permits a change of a participant's own dose of the study treatment (review item 18).
 * Not a permission: a sentence about cohorts or the escalation scheme ("enroll an additional 3 subjects at this
 * dose level", "advance to dose level 2", "the dose will be increased for the next cohort") unless it names one
 * participant; the definition of a dose level; a titration, reduction or increase of ANOTHER drug ("titrate the
 * dose of their antihypertensive"). A titration counts only beside the word dose or for the study treatment, and
 * an escalation or dose level only with one participant named.
 */
export function permitsParticipantDoseChange(sentence: string, analysis?: Record<string, unknown> | null): boolean {
  const x = sentence.toLowerCase();
  if (!/dose/.test(x) || DOSE_DENIAL.test(x)) return false;
  const per = PER_PARTICIPANT.test(x);
  if (COHORT.test(x) && !per) return false;
  // "the dose of <something>" names what is dosed: the study treatment, or another drug (which does not count).
  const names = treatmentNamesOf(analysis).map((n) => n.toLowerCase());
  const studyNamed = STUDY_TREATMENT_WORDS.test(x) || names.some((n) => x.includes(n));
  const dosed = /\b(?:dose|doses|dosing)\s+of\s+(?:(?:the|their|his|her|any)\s+)?([a-z][a-z-]*)/.exec(x);
  // A function or number word after "dose of" ("a reduction in dose of one level") names no drug. The captured
  // word is the study drug when it is the first word of one of the study's treatment names ("insulin" for
  // "Insulin glargine U100 (Lantus)").
  const firstWords = names.map((n) => n.split(/[\s(/,]+/)[0]).filter((w) => w.length >= 3);
  const namesNoDrug = !!dosed && /^(?:a|an|one|two|three|four|five|half|each|every|this|that|which|at|up|no|level|levels|step|steps)$/.test(dosed[1]);
  if (dosed && !namesNoDrug && !studyNamed && !firstWords.includes(dosed[1]) && !/^(?:study|investigational|imp|trial)$/.test(dosed[1])) return false;
  if (DOSE_CHANGE_WORD.test(x)) return true;
  const studyDosed = studyNamed || (!!dosed && firstWords.includes(dosed[1]));
  if (/titrat/.test(x) && (studyDosed || /\bdos\w*\W+(?:\w+\W+){0,3}titrat|titrat\w*\W+(?:\w+\W+){0,3}dos/.test(x))) return true;
  if (/escalat|dose level/.test(x) && per) return true;
  return false;
}

/** Everything the reading says under the named keys, as one lower-case string. */
export function corpusOf(analysis: any, keys: string[]): string {
  const parts: string[] = [];
  const walk = (v: unknown, depth: number) => {
    if (v == null || depth > 6) return;
    if (typeof v === "string") parts.push(v);
    else if (typeof v === "number" || typeof v === "boolean") parts.push(String(v));
    else if (Array.isArray(v)) for (const i of v) walk(i, depth + 1);
    else if (typeof v === "object") for (const i of Object.values(v as object)) walk(i, depth + 1);
  };
  // The study's own interventions: a product the protocol only mentions is not the study's (studyProducts.ts).
  for (const k of keys) walk(k === "interventions" ? studyInterventions<unknown>(analysis) : analysis?.[k], 0);
  return parts.join(" | ").toLowerCase();
}

/**
 * True when the protocol (reading or full text) says the study drug dose may not be changed ("no dose
 * reduction", "dose reduction is not allowed", "only interruption") and nowhere permits a change. That is a
 * statement about the action-taken list, so CDASH's dose-change options must not be offered.
 */
export function protocolForbidsDoseChange(analysis: Record<string, unknown> | null | undefined, protocolText?: string | null): boolean {
  const corpus = `${corpusOf(analysis, ["interventions", "safety", "treatment", "dosing", "dosageModifications", "doseModification", "doseModifications", "stoppingRules"])} | ${String(protocolText ?? "").toLowerCase()}`;
  const sentences = corpus.split(/[.;|\n]+/).map((x) => x.trim()).filter(Boolean);
  const denies = sentences.some(isStudyWideDoseDenial);
  const permits = sentences.some((x) => permitsParticipantDoseChange(x, analysis));
  return denies && !permits;
}
